import React, { useState, useEffect } from "react"
import { Button, Form, Modal } from "react-bootstrap"
import { QRCodeSVG } from "qrcode.react"

import { PasswordInput } from "@components/PasswordInput"
import { ErrorHandling } from "@helpers/GlobalFunctions"
import { setupSecurityQuestions, setupTotp, verifyTotp, continueLater } from "@api/authenticate"
import { toast } from "@components/ToastExtended"

const SetupSecurityModal = ({
    show,
    setupToken,
    setupStep,
    setSetupStep,
    securityQuestionsList,
    totpProvisioningUri,
    setTotpProvisioningUri,
    onComplete,
    onCancel,
}) => {
    const [questionsForm, setQuestionsForm] = useState([
        { id: "", question: "", answer: "" },
        { id: "", question: "", answer: "" },
        { id: "", question: "", answer: "" },
    ])
    const [totpCode, setTotpCode] = useState("")
    const [submitting, setSubmitting] = useState(false)
    const [continuingLater, setContinuingLater] = useState(false)
    const [error, setError] = useState("")

    const handleQuestionChange = (index, field, value) => {
        setQuestionsForm((prev) => {
            const next = [...prev]
            next[index] = { ...next[index], [field]: value }
            if (field === "id") {
                const q = securityQuestionsList.find((x) => String(x.id) === String(value))
                next[index].question = q ? q.question : ""
            }
            return next
        })
        setError("")
    }

    const handleSaveQuestions = async () => {
        const hasEmpty = questionsForm.some((q) => !q.id || !q.answer?.trim())
        const uniqueIds = new Set(questionsForm.map((q) => q.id))
        if (uniqueIds.size !== 3) {
            setError("Please select 3 different questions and provide an answer for each.")
            return
        }
        if (hasEmpty) {
            setError("Please select 3 questions and provide an answer for each.")
            return
        }
        setSubmitting(true)
        setError("")
        try {
            const payload = questionsForm.map((q) => ({
                ID: parseInt(q.id, 10),
                Question: q.question,
                Answer: q.answer.trim(),
            }))
            await setupSecurityQuestions(setupToken || null, payload)
            setSetupStep("totp")
            setTotpProvisioningUri(null)
            setTotpCode("")
        } catch (err) {
            setError(ErrorHandling(err))
        }
        setSubmitting(false)
    }

    useEffect(() => {
        if (show && setupStep === "totp" && !totpProvisioningUri) {
            setSubmitting(true)
            setupTotp(setupToken || null)
                .then((res) => {
                    setTotpProvisioningUri(res?.data?.provisioning_uri || null)
                })
                .catch(() => setError("Failed to load authenticator setup."))
                .finally(() => setSubmitting(false))
        }
    }, [show, setupStep, setupToken, totpProvisioningUri, setTotpProvisioningUri])

    const handleVerifyTotp = async () => {
        if (!totpCode.trim() || totpCode.length < 6) {
            setError("Please enter the 6-digit code from your authenticator app.")
            return
        }
        setSubmitting(true)
        setError("")
        try {
            await verifyTotp(setupToken || null, totpCode.trim())
            onComplete()
        } catch (err) {
            setError(ErrorHandling(err))
        }
        setSubmitting(false)
    }

    const handleContinueLater = async () => {
        if (!setupToken) {
            setError("Cannot continue later - please complete security setup to access your account.")
            return
        }

        setContinuingLater(true)
        setError("")

        try {
            await continueLater(setupToken)
            toast.show({
                type: "success",
                title: "Email sent",
                message: "A link to continue your signup has been sent to your email. You have 3 days to complete it.",
            })
            window.location.href = "/"
        } catch (err) {
            setError(ErrorHandling(err))
        } finally {
            setContinuingLater(false)
        }
    }

    return (
        <Modal show={show} backdrop="static" keyboard={false} centered size="lg">
            <Modal.Header>
                <Modal.Title>
                    {setupStep === "questions" && "Step 1: Security questions"}
                    {setupStep === "totp" && "Step 2: Authenticator"}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {setupStep === "questions" && (
                    <>
                        <p className="text-muted small">
                            Choose 3 security questions and provide answers. You will use these to recover your account.
                        </p>
                        {[0, 1, 2].map((index) => (
                            <div key={index} className="mb-3">
                                <Form.Label>Question {index + 1}</Form.Label>
                                <Form.Select
                                    value={questionsForm[index].id}
                                    onChange={(e) => handleQuestionChange(index, "id", e.target.value)}
                                >
                                    <option value="">Select a question</option>
                                    {securityQuestionsList.map((q) => (
                                        <option key={q.id} value={q.id}>
                                            {q.question}
                                        </option>
                                    ))}
                                </Form.Select>
                                <div className="mt-1">
                                    <PasswordInput
                                        placeholder="Your answer"
                                        name={`security_answer_${index}`}
                                        value={questionsForm[index].answer}
                                        onChange={(e) =>
                                            handleQuestionChange(index, "answer", e.target.value)
                                        }
                                    />
                                </div>
                            </div>
                        ))}
                        {error && <p className="text-danger small">{error}</p>}
                    </>
                )}
                {setupStep === "totp" && (
                    <>
                        <p className="text-muted small">
                            Scan the QR code with Google Authenticator (or another TOTP app), then enter the 6-digit code below.
                        </p>
                        {submitting && !totpProvisioningUri && <p>Loading...</p>}
                        {totpProvisioningUri && (
                            <div className="text-center mb-3">
                                {/* <img
                                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(totpProvisioningUri)}`}
                                    alt="QR code for authenticator"
                                    style={{ width: 200, height: 200 }}
                                /> */}
                                <QRCodeSVG value={totpProvisioningUri} size={200} />
                            </div>
                        )}
                        <Form.Group>
                            <Form.Label>Verification code</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="000000"
                                maxLength={6}
                                value={totpCode}
                                onChange={(e) => {
                                    setTotpCode(e.target.value.replace(/\D/g, ""))
                                    setError("")
                                }}
                            />
                        </Form.Group>
                        {error && <p className="text-danger small mt-2">{error}</p>}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between">
                {setupToken && (
                    <Button
                        variant="outline-secondary"
                        onClick={handleContinueLater}
                        disabled={submitting || continuingLater}
                    >
                        {continuingLater ? "Sending..." : "Continue Later"}
                    </Button>
                )}
                <div className="ms-auto">
                    {setupStep === "questions" && (
                        <Button variant="primary" onClick={handleSaveQuestions} disabled={submitting || continuingLater}>
                            {submitting ? "Saving..." : "Continue to Authenticator"}
                        </Button>
                    )}
                    {setupStep === "totp" && (
                        <Button variant="primary" onClick={handleVerifyTotp} disabled={submitting || continuingLater}>
                            {submitting ? "Verifying..." : "Verify and finish"}
                        </Button>
                    )}
                </div>
            </Modal.Footer>
        </Modal>
    )
}

export default SetupSecurityModal
